// واجهة "صلاحية المستخدمين" (أدمن فقط): لستة الحسابات ككروت، والضغط على أي
// حساب يفتح نافذة تعديل وحدة فيها كلشي: بيانات الدخول، البوكسات، والواجهات.
//
// كل بوكس/واجهة = مربع: المفعّل عليه ✕ للشيل، والمتاح عليه ＋ للإضافة -
// حتى الأزرار الثابتة بلوحة الحسابات القديمة (تنحفظ بـhiddenBuiltIns لما تنشال).
// القواعد (شنو ينضاف لكل حساب) يفرضها الباك اند: config/accountAccess.js
import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Typography,
  Paper,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  Snackbar,
  Alert,
  Box,
  IconButton,
  Tooltip,
  Avatar,
  InputAdornment,
  Collapse,
  useMediaQuery,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { apiFetch, API_BASE } from "./utils/apiClient";
import { PERMISSION_BOXES } from "./utils/userProfile";
import { getAccountBuiltIns, FORMER_STAFF_ACCOUNTS } from "./utils/accountBuiltIns";

// النشطين فوك، والموقوفين جوة، والحسابات بدون دخول بالأخير - وبعدها أبجدياً
const statusRank = (user) => (user.disabled === false ? 0 : user.disabled === true ? 1 : 2);

const emptyForm = {
  loginName: "",
  displayName: "",
  password: "",
  permissions: [],
  grantedDashboards: [],
  hiddenBuiltIns: [],
  dashboardType: "boxes",
  accessLevel: "standard",
};

// قواعد الحساب الجديد/غير الثابت حسب مستوى الصلاحية اللي يختاره المدير
// (نفس "full" و"staff" بالباك اند: config/accountAccess.js)
const NEW_ACCOUNT_RULES = { accessRole: "staff", allowedBoxes: null, dashboardsRule: "none" };
const FULL_ACCESS_RULES = { accessRole: "full", allowedBoxes: null, dashboardsRule: "free" };
const rulesForLevel = (level) => (level === "full" ? FULL_ACCESS_RULES : NEW_ACCOUNT_RULES);

const ACCESS_LEVEL_OPTIONS = [
  {
    value: "full",
    icon: "🛡️",
    title: "صلاحيات إدارية كاملة",
    description: "يمكن إضافة أي بوكس أو واجهة لهذا الحساب مثل حساب بنين (مثلاً واجهة المدير والنظام المحاسبي).",
  },
  {
    value: "standard",
    icon: "👤",
    title: "صلاحيات موظف",
    description: "مثل حسابات الموظفين الحالية (المصمم وغيره): يمكن إضافة البوكسات المسموحة فقط بدون واجهات الموظفين.",
  },
];

// لون صورة الحساب حسب نوعه (بدون أي تسمية ظاهرة)
const ROLE_INFO = {
  admin: { color: "#263238" },
  manager: { color: "#6a1b9a" },
  full: { color: "#00695c" },
  staff: { color: "#1565c0" },
  illustrator: { color: "#c2185b" },
  workshop: { color: "#e65100" },
  locked: { color: "#546e7a" },
};

const ROLE_NOTES = {
  illustrator: "يمكن إضافة البوكسات الأساسية فقط لهذا الحساب (طلب إجازة، طلب مواد) إضافةً إلى البوكسات الخاصة بالرسم.",
  workshop: "يمكن إضافة البوكسات الأساسية فقط لهذا الحساب (طلب إجازة، طلب مواد).",
  locked: "لا يمكن إضافة أي بوكس أو واجهة لهذا الحساب حالياً حفاظاً على سرية المعلومات والأرقام، ويمكن إزالة الموجود.",
};

const formatDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB") : "—");
const roleOf = (user) => (user?.username === "admin" ? "admin" : user?.accessRole || "staff");
const sameSet = (a, b) => a.length === b.length && a.every((x) => b.includes(x));

// ===== حالة كل بوكس/واجهة لحساب (مشتركة بين الكارت ونافذة التعديل) =====
const describeAccess = ({ username, dashboardType, permissions, grantedDashboards, hiddenBuiltIns }, dashboards) => {
  const builtIns = getAccountBuiltIns(username, dashboardType);
  const hidden = hiddenBuiltIns || [];
  const builtInBoxIds = new Set((builtIns?.boxes || []).map((b) => b.key));

  const boxes = PERMISSION_BOXES.map((box) => {
    const builtIn = builtInBoxIds.has(box.key);
    const id = `box:${box.key}`;
    return {
      kind: "box",
      id,
      key: box.key,
      label: box.label,
      icon: box.icon,
      builtIn,
      active: (builtIn && !hidden.includes(id)) || permissions.includes(box.key),
    };
  });
  const extras = (builtIns?.extras || []).map((item) => ({ ...item, builtIn: true, active: !hidden.includes(item.id) }));

  const builtInPaths = new Set((builtIns?.interfaces || []).map((d) => d.path));
  const interfaces = [
    ...(builtIns?.interfaces || []).map((d) => ({ ...d, builtIn: true, active: !hidden.includes(d.id) })),
    ...dashboards
      .filter((d) => !builtInPaths.has(d.path) && d.path !== builtIns?.home)
      .map((d) => ({
        kind: "grant",
        id: `grant:${d.path}`,
        label: d.label,
        icon: "🖥️",
        path: d.path,
        archived: !!d.archived,
        builtIn: false,
        active: grantedDashboards.some((g) => g.path === d.path),
      })),
  ];

  return { builtIns, boxes, extras, interfaces };
};

// ===== مربع وحدة (بوكس أو واجهة) =====
const Tile = ({ item, onRemove, onAdd, locked }) => {
  const active = item.active;
  const clickable = !active && !locked;
  return (
    <Box
      onClick={clickable ? onAdd : undefined}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        px: 1.5,
        py: 1,
        minHeight: 48,
        borderRadius: 2,
        border: active ? "1.5px solid #66bb6a" : "1.5px dashed #c5cae9",
        bgcolor: active ? "#f1f8f2" : locked ? "#f5f5f5" : "#fafbff",
        color: locked ? "text.disabled" : "text.primary",
        cursor: clickable ? "pointer" : "default",
        transition: "all .15s",
        "&:hover": clickable ? { borderColor: "#3f51b5", bgcolor: "#eef0fb" } : undefined,
      }}
    >
      <Typography component="span" fontSize={18} lineHeight={1}>
        {item.icon || "▫️"}
      </Typography>
      <Typography fontSize={14} fontWeight={active ? 700 : 500} sx={{ flex: 1, lineHeight: 1.3 }}>
        {item.label}
      </Typography>
      {active ? (
        <Tooltip title="إزالة">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            sx={{ color: "#9e9e9e", "&:hover": { color: "#d32f2f", bgcolor: "#fdecea" } }}
            aria-label={`إزالة ${item.label}`}
          >
            ✕
          </IconButton>
        </Tooltip>
      ) : locked ? (
        <Tooltip title="غير متاح لهذا الحساب">
          <span>🔒</span>
        </Tooltip>
      ) : (
        <Typography component="span" fontWeight={900} fontSize={20} color="primary" lineHeight={1}>
          ＋
        </Typography>
      )}
    </Box>
  );
};

// ===== اختيار مستوى الصلاحية (عند إنشاء حساب، أو تعديل حساب غير ثابت) =====
const AccessLevelPicker = ({ value, onChange }) => (
  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
    {ACCESS_LEVEL_OPTIONS.map((option) => {
      const selected = value === option.value;
      return (
        <Box
          key={option.value}
          role="radio"
          aria-checked={selected}
          tabIndex={0}
          onClick={() => onChange(option.value)}
          onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onChange(option.value)}
          sx={{
            p: 2,
            borderRadius: 2.5,
            cursor: "pointer",
            border: selected ? "2px solid #3f51b5" : "1.5px solid #e0e3ef",
            bgcolor: selected ? "#eef0fb" : "#fff",
            transition: "all .15s",
            "&:hover": { borderColor: "#3f51b5" },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <Typography fontSize={20} lineHeight={1}>
              {option.icon}
            </Typography>
            <Typography fontWeight={800} fontSize={15} sx={{ flex: 1 }}>
              {option.title}
            </Typography>
            {selected && <Chip size="small" color="primary" label="✓ مختار" />}
          </Box>
          <Typography fontSize={13} color="text.secondary" sx={{ lineHeight: 1.6 }}>
            {option.description}
          </Typography>
        </Box>
      );
    })}
  </Box>
);

const TileGrid = ({ children }) => (
  <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))", gap: 1 }}>{children}</Box>
);

const SectionTitle = ({ icon, title, count, hint }) => (
  <Box sx={{ mb: 1.5 }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Typography fontSize={18}>{icon}</Typography>
      <Typography fontWeight={800} fontSize={17}>
        {title}
      </Typography>
      {count !== undefined && <Chip size="small" label={count} sx={{ fontWeight: 700 }} />}
    </Box>
    {hint && (
      <Typography fontSize={13} color="text.secondary" sx={{ mt: 0.25 }}>
        {hint}
      </Typography>
    )}
  </Box>
);

const SubLabel = ({ children }) => (
  <Typography fontSize={13} fontWeight={700} color="text.secondary" sx={{ mt: 2, mb: 1 }}>
    {children}
  </Typography>
);

const PasswordLine = ({ password }) => {
  const [visible, setVisible] = useState(false);
  if (!password) {
    return (
      <Tooltip title="كلمة المرور الحالية غير محفوظة. عيّن كلمة مرور جديدة من نافذة التعديل لتظهر هنا.">
        <Typography fontSize={13} color="text.secondary">
          🔑 غير محفوظة
        </Typography>
      </Tooltip>
    );
  }
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
      <Typography fontSize={13} color="text.secondary">
        🔑
      </Typography>
      <Typography fontSize={13} sx={{ fontFamily: "monospace", direction: "ltr" }}>
        {visible ? password : "••••••"}
      </Typography>
      <IconButton size="small" onClick={() => setVisible((v) => !v)} aria-label="إظهار كلمة المرور">
        {visible ? "🙈" : "👁️"}
      </IconButton>
      <IconButton size="small" onClick={() => navigator.clipboard?.writeText(password)} aria-label="نسخ كلمة المرور">
        📋
      </IconButton>
    </Box>
  );
};

// ===== كارت حساب باللستة =====
const AccountCard = ({ user, dashboards, onOpen, onToggleDisabled, onOpenWorkspace }) => {
  const role = roleOf(user);
  const info = ROLE_INFO[role];
  const access = describeAccess(
    {
      username: user.username,
      dashboardType: user.dashboardType,
      permissions: user.permissions || [],
      grantedDashboards: user.grantedDashboards || [],
      hiddenBuiltIns: user.hiddenBuiltIns || [],
    },
    dashboards
  );
  const boxCount = [...access.boxes, ...access.extras].filter((i) => i.active).length;
  const dashCount = access.interfaces.filter((i) => i.active).length;
  const status =
    user.disabled === null
      ? { label: "بدون حساب دخول", color: "default" }
      : user.disabled
      ? { label: "موقوف", color: "error" }
      : { label: "نشط", color: "success" };

  return (
    <Paper
      onClick={onOpen}
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 3,
        border: "1px solid #e3e6ef",
        cursor: "pointer",
        opacity: user.disabled ? 0.7 : 1,
        display: "flex",
        flexDirection: "column",
        gap: 1.25,
        transition: "all .15s",
        "&:hover": { borderColor: info.color, boxShadow: "0 6px 20px rgba(30,40,90,.08)", transform: "translateY(-2px)" },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <Avatar sx={{ bgcolor: info.color, width: 44, height: 44, fontWeight: 800 }}>
          {String(user.displayName || "?").trim().charAt(0)}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography fontWeight={800} fontSize={16} noWrap>
            {user.displayName}
          </Typography>
          <Typography fontSize={12} color="text.secondary" noWrap sx={{ direction: "ltr", textAlign: "right" }}>
            {user.loginName}
          </Typography>
        </Box>
        <Chip size="small" label={status.label} color={status.color} />
      </Box>

      <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
        {role !== "admin" && (
          <>
            <Chip size="small" variant="outlined" label={`📦 ${boxCount} بوكسات`} />
            <Chip size="small" variant="outlined" label={`🖥️ ${dashCount} واجهات`} />
          </>
        )}
      </Box>

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
        <PasswordLine password={user.password} />
        <Typography fontSize={12} color="text.secondary">
          تاريخ الإضافة: {formatDate(user.createdAt)}
          {user.disabled && ` · تاريخ الإيقاف: ${formatDate(user.disabledAt)}`}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", gap: 1, mt: 0.5 }} onClick={(e) => e.stopPropagation()}>
        <Button variant="contained" size="small" onClick={onOpen} sx={{ flex: 1, borderRadius: 2, fontWeight: 700 }}>
          تعديل الصلاحيات
        </Button>
        {user.username !== "admin" && user.disabled !== null && (
          <Button
            size="small"
            variant="outlined"
            color={user.disabled ? "success" : "error"}
            onClick={onToggleDisabled}
            sx={{ borderRadius: 2 }}
          >
            {user.disabled ? "تفعيل" : "إيقاف"}
          </Button>
        )}
        {user.dashboardType === "boxes" && (
          <Button size="small" variant="outlined" onClick={onOpenWorkspace} sx={{ borderRadius: 2 }}>
            فتح اللوحة
          </Button>
        )}
      </Box>
    </Paper>
  );
};

const UserPermissionsDashboard = () => {
  const navigate = useNavigate();
  const fullScreenDialog = useMediaQuery("(max-width:700px)");
  const [users, setUsers] = useState([]);
  const [dashboards, setDashboards] = useState([]);
  const [loading, setLoading] = useState(true);
  // null = مغلق، { mode: "create" } أو { mode: "edit", user }
  const [dialog, setDialog] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [initialForm, setInitialForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showFormerStaff, setShowFormerStaff] = useState(false);
  const [showArchivedDashboards, setShowArchivedDashboards] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  // إضافة واجهة موظف لغير بنين: مقفولة لحد ما المدير يأكّد بالتحذير
  const [dashboardsUnlocked, setDashboardsUnlocked] = useState(false);
  const [pendingGrant, setPendingGrant] = useState(null);

  const notify = (message, severity = "success") => setSnackbar({ open: true, message, severity });

  const loadData = async () => {
    setLoading(true);
    try {
      const [usersRes, dashboardsRes] = await Promise.all([
        apiFetch(`${API_BASE}/admin/users`),
        apiFetch(`${API_BASE}/admin/dashboards`),
      ]);
      const usersData = await usersRes.json();
      const dashboardsData = await dashboardsRes.json();

      if (!usersRes.ok) throw new Error(usersData.error || "فشل تحميل المستخدمين");

      setUsers(usersData);
      setDashboards(Array.isArray(dashboardsData) ? dashboardsData : []);
    } catch (error) {
      notify(`❌ ${error.message}`, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const visibleUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    return [...users]
      .filter((u) => statusFilter === "all" || (statusFilter === "active" ? u.disabled === false : u.disabled !== false))
      .filter(
        (u) =>
          !q ||
          String(u.displayName || "").toLowerCase().includes(q) ||
          String(u.loginName || "").toLowerCase().includes(q)
      )
      .sort(
        (a, b) =>
          (a.username === "admin" ? -1 : 0) - (b.username === "admin" ? -1 : 0) ||
          statusRank(a) - statusRank(b) ||
          String(a.displayName).localeCompare(String(b.displayName), "ar")
      );
  }, [users, search, statusFilter]);
  // الموظفون السابقون = الموظفات القديمات + أي حساب موقوف (ينزل تلقائياً عند
  // الإيقاف، ويرجع فوك عند التفعيل)
  const isFormer = (u) => FORMER_STAFF_ACCOUNTS.includes(u.username) || u.disabled === true;
  const currentUsers = visibleUsers.filter((u) => !isFormer(u));
  const formerUsers = visibleUsers.filter(isFormer);
  // فلتر "الموقوفة" يعرض القسم مفتوح
  const formerOpen = showFormerStaff || statusFilter === "disabled";
  const counts = {
    all: users.length,
    active: users.filter((u) => u.disabled === false).length,
    disabled: users.filter((u) => u.disabled !== false).length,
  };

  const openDialog = (mode, user, values) => {
    setForm(values);
    setInitialForm(values);
    setDashboardsUnlocked(false);
    setShowArchivedDashboards(false);
    setShowAdvanced(false);
    setDialog({ mode, user });
  };

  const openCreate = () => openDialog("create", null, emptyForm);

  const openEdit = (user) =>
    openDialog("edit", user, {
      loginName: user.loginName || user.username,
      displayName: user.displayName || "",
      password: "",
      permissions: user.permissions || [],
      grantedDashboards: user.grantedDashboards || [],
      hiddenBuiltIns: user.hiddenBuiltIns || [],
      dashboardType: user.dashboardType || "legacy",
      accessLevel: user.accessLevel || "standard",
    });

  const isDirty =
    !!dialog &&
    (form.loginName !== initialForm.loginName ||
      form.displayName !== initialForm.displayName ||
      !!form.password ||
      form.dashboardType !== initialForm.dashboardType ||
      form.accessLevel !== initialForm.accessLevel ||
      !sameSet(form.permissions, initialForm.permissions) ||
      !sameSet(form.hiddenBuiltIns, initialForm.hiddenBuiltIns) ||
      !sameSet(
        form.grantedDashboards.map((d) => d.path),
        initialForm.grantedDashboards.map((d) => d.path)
      ));

  const closeDialog = () => {
    if (saving) return;
    if (isDirty && !window.confirm("لديك تغييرات غير محفوظة. هل تريد الخروج دون حفظ؟")) return;
    setDialog(null);
  };

  const editingUser = dialog?.mode === "edit" ? dialog.user : null;
  const editingAdmin = editingUser?.username === "admin";
  // المدير يختار المستوى عند الإنشاء، أو يغيّره لحساب مو ثابت القواعد (مثل الحسابات الجديدة)
  const levelChoosable = !!dialog && !editingAdmin && (!editingUser || !!editingUser.canChooseAccessLevel);
  const rules =
    editingUser && !levelChoosable
      ? {
          accessRole: editingUser.accessRole || "staff",
          allowedBoxes: editingUser.allowedBoxes ?? null,
          dashboardsRule: editingUser.dashboardsRule || "none",
        }
      : rulesForLevel(form.accessLevel);

  // الرجوع لصلاحيات موظف: الواجهات اللي انضافت بهذي الجلسة تنشال (الموجودة أصلاً تبقى)
  const setAccessLevel = (level) =>
    setForm((f) => ({
      ...f,
      accessLevel: level,
      grantedDashboards:
        level === "full"
          ? f.grantedDashboards
          : f.grantedDashboards.filter((g) => initialForm.grantedDashboards.some((i) => i.path === g.path)),
    }));
  const originalPermissions = editingUser?.permissions || [];

  const access = describeAccess(
    {
      username: editingUser?.username,
      dashboardType: form.dashboardType,
      permissions: form.permissions,
      grantedDashboards: form.grantedDashboards,
      hiddenBuiltIns: form.hiddenBuiltIns,
    },
    dashboards
  );

  // بوكس جديد ينضاف إذا مسموح لهذا الحساب، أو كان عنده أصلاً
  const canAdd = (item) => {
    if (item.builtIn) return true;
    if (item.kind === "box") {
      return !rules.allowedBoxes || rules.allowedBoxes.includes(item.key) || originalPermissions.includes(item.key);
    }
    if (item.kind === "grant") {
      const hadIt = (initialForm.grantedDashboards || []).some((g) => g.path === item.path);
      return hadIt || rules.dashboardsRule !== "none";
    }
    return false;
  };

  const setHidden = (id, hide) =>
    setForm((f) => ({
      ...f,
      hiddenBuiltIns: hide ? [...new Set([...f.hiddenBuiltIns, id])] : f.hiddenBuiltIns.filter((x) => x !== id),
    }));

  const removeItem = (item) => {
    if (item.kind === "box") {
      setForm((f) => ({ ...f, permissions: f.permissions.filter((k) => k !== item.key) }));
      if (item.builtIn) setHidden(item.id, true);
    } else if (item.kind === "grant") {
      setForm((f) => ({ ...f, grantedDashboards: f.grantedDashboards.filter((g) => g.path !== item.path) }));
    } else {
      setHidden(item.id, true);
    }
  };

  const grantDashboard = (item) =>
    setForm((f) => ({
      ...f,
      grantedDashboards: [...f.grantedDashboards.filter((g) => g.path !== item.path), { label: item.label, path: item.path }],
    }));

  const addItem = (item) => {
    if (item.builtIn) {
      setHidden(item.id, false);
    } else if (item.kind === "box") {
      setForm((f) => ({ ...f, permissions: [...new Set([...f.permissions, item.key])] }));
    } else if (item.kind === "grant") {
      const hadIt = (initialForm.grantedDashboards || []).some((g) => g.path === item.path);
      if (rules.dashboardsRule === "confirm" && !dashboardsUnlocked && !hadIt) {
        setPendingGrant(item);
        return;
      }
      grantDashboard(item);
    }
  };

  const handleSave = async () => {
    if (!dialog) return;
    if (!form.loginName.trim() || !form.displayName.trim()) {
      notify("❌ اسم الدخول والاسم الظاهر مطلوبان", "error");
      return;
    }
    if (dialog.mode === "create" && form.password.length < 6) {
      notify("❌ يجب أن تتكون كلمة المرور من 6 أحرف على الأقل", "error");
      return;
    }

    setSaving(true);
    try {
      const isCreate = dialog.mode === "create";
      const body = {
        loginName: form.loginName.trim(),
        displayName: form.displayName.trim(),
        permissions: form.permissions,
        grantedDashboards: form.grantedDashboards,
        ...(dashboardsUnlocked ? { confirmExtraDashboards: true } : {}),
        ...(levelChoosable ? { accessLevel: form.accessLevel } : {}),
        ...(isCreate ? { password: form.password } : form.password ? { newPassword: form.password } : {}),
        ...(!isCreate && !editingAdmin
          ? { dashboardType: form.dashboardType, hiddenBuiltIns: form.hiddenBuiltIns }
          : {}),
      };

      const res = await apiFetch(
        isCreate ? `${API_BASE}/admin/users` : `${API_BASE}/admin/users/${encodeURIComponent(dialog.user.username)}`,
        {
          method: isCreate ? "POST" : "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "فشل الحفظ");

      notify(isCreate ? "✅ تم إنشاء المستخدم" : "✅ تم الحفظ");
      setDialog(null);
      loadData();
    } catch (error) {
      notify(`❌ ${error.message}`, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleDisabled = async (user) => {
    if (!user.disabled && !window.confirm(`هل أنت متأكد من إيقاف حساب ${user.displayName}؟ لن يتمكن من تسجيل الدخول.`)) return;
    try {
      const action = user.disabled ? "enable" : "disable";
      const res = await apiFetch(`${API_BASE}/admin/users/${encodeURIComponent(user.username)}/${action}`, {
        method: "POST",
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "فشل تغيير الحالة");
      notify(user.disabled ? "✅ تم تفعيل الحساب" : "✅ تم إيقاف الحساب");
      loadData();
    } catch (error) {
      notify(`❌ ${error.message}`, "error");
    }
  };

  const renderCard = (user) => (
    <AccountCard
      key={user.username}
      user={user}
      dashboards={dashboards}
      onOpen={() => openEdit(user)}
      onToggleDisabled={() => handleToggleDisabled(user)}
      onOpenWorkspace={() => navigate(`/workspace/${user.username}`)}
    />
  );

  // ===== أقسام نافذة التعديل =====
  const allBoxes = [...access.boxes, ...access.extras];
  const activeBoxes = allBoxes.filter((i) => i.active);
  const addableBoxes = allBoxes.filter((i) => !i.active && canAdd(i));
  const lockedBoxes = allBoxes.filter((i) => !i.active && !canAdd(i));
  const activeDashboards = access.interfaces.filter((i) => i.active);
  const addableDashboards = access.interfaces.filter((i) => !i.active && canAdd(i));
  const addableCurrent = addableDashboards.filter((i) => !i.archived);
  const addableArchived = addableDashboards.filter((i) => i.archived);
  const role = editingAdmin ? "admin" : rules.accessRole;
  const roleInfo = ROLE_INFO[role];

  const cardGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 2 };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 8 }} dir="rtl">
      {/* العنوان */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, flexWrap: "wrap", mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={900}>
            🔐 صلاحية المستخدمين
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 0.5 }}>
            اضغط على أي حساب لعرض البوكسات والواجهات الخاصة به وتعديلها
          </Typography>
        </Box>
        <Button variant="contained" size="large" onClick={openCreate} sx={{ borderRadius: 2.5, fontWeight: 800, px: 3 }}>
          ➕ مستخدم جديد
        </Button>
      </Box>

      {/* البحث والفلتر */}
      <Paper elevation={0} sx={{ p: 1.5, mb: 3, borderRadius: 3, border: "1px solid #e3e6ef", display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
        <TextField
          size="small"
          placeholder="ابحث بالاسم أو اسم الدخول"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1, minWidth: 220 }}
          InputProps={{ startAdornment: <InputAdornment position="start">🔍</InputAdornment> }}
        />
        {[
          { key: "all", label: "الكل" },
          { key: "active", label: "النشطة" },
          { key: "disabled", label: "الموقوفة" },
        ].map((f) => (
          <Chip
            key={f.key}
            label={`${f.label} (${counts[f.key]})`}
            onClick={() => setStatusFilter(f.key)}
            color={statusFilter === f.key ? "primary" : "default"}
            variant={statusFilter === f.key ? "filled" : "outlined"}
            sx={{ fontWeight: 700 }}
          />
        ))}
      </Paper>

      {/* الحسابات */}
      {loading && users.length === 0 ? (
        <Typography color="text.secondary">جارِ التحميل...</Typography>
      ) : (
        <>
          <Box sx={cardGrid}>{currentUsers.map(renderCard)}</Box>
          {!loading && visibleUsers.length === 0 && (
            <Typography color="text.secondary" sx={{ mt: 2 }}>
              لا توجد حسابات مطابقة للبحث
            </Typography>
          )}

          {formerUsers.length > 0 && (
            <Box sx={{ mt: 4 }}>
              <Button onClick={() => setShowFormerStaff((v) => !v)} sx={{ fontWeight: 700, color: "text.secondary" }}>
                📁 الموظفون السابقون والحسابات الموقوفة ({formerUsers.length}) {formerOpen ? "▲" : "▼"}
              </Button>
              <Collapse in={formerOpen}>
                <Box sx={{ ...cardGrid, mt: 1.5 }}>{formerUsers.map(renderCard)}</Box>
              </Collapse>
            </Box>
          )}
        </>
      )}

      {/* ===== نافذة التعديل ===== */}
      <Dialog
        open={!!dialog}
        onClose={closeDialog}
        fullWidth
        maxWidth="md"
        fullScreen={fullScreenDialog}
        dir="rtl"
        PaperProps={{ sx: { borderRadius: fullScreenDialog ? 0 : 3 } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5, borderBottom: "1px solid #eee", py: 2 }}>
          <Avatar sx={{ bgcolor: roleInfo?.color, width: 48, height: 48, fontWeight: 800 }}>
            {dialog?.mode === "create" ? "＋" : String(editingUser?.displayName || "?").trim().charAt(0)}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography fontWeight={900} fontSize={20} noWrap>
              {dialog?.mode === "create" ? "مستخدم جديد" : editingUser?.displayName}
            </Typography>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
              {editingUser && (
                <Typography fontSize={12} color="text.secondary">
                  تاريخ الإضافة: {formatDate(editingUser.createdAt)}
                  {editingUser.disabled && ` · تاريخ الإيقاف: ${formatDate(editingUser.disabledAt)}`}
                </Typography>
              )}
            </Box>
          </Box>
          <IconButton onClick={closeDialog} aria-label="إغلاق">
            ✕
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ bgcolor: "#f7f8fc", p: { xs: 1.5, sm: 3 } }}>
          {/* بيانات الدخول */}
          <Paper elevation={0} sx={{ p: 2.5, mt: 2, borderRadius: 3, border: "1px solid #e3e6ef" }}>
            <SectionTitle icon="👤" title="بيانات الدخول" />
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" }, gap: 2 }}>
              <TextField
                label="اسم الدخول"
                value={form.loginName}
                disabled={editingAdmin}
                onChange={(e) => setForm((f) => ({ ...f, loginName: e.target.value }))}
                inputProps={{ dir: "ltr" }}
                helperText="يُستخدم في شاشة تسجيل الدخول"
              />
              <TextField
                label="الاسم الظاهر"
                value={form.displayName}
                onChange={(e) => setForm((f) => ({ ...f, displayName: e.target.value }))}
                helperText="يظهر في اللوحة وقوائم الموظفين"
              />
              <TextField
                label={dialog?.mode === "create" ? "كلمة المرور" : "كلمة مرور جديدة"}
                value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                inputProps={{ dir: "ltr" }}
                helperText={dialog?.mode === "create" ? "6 أحرف على الأقل" : "اتركها فارغة للإبقاء على كلمة المرور الحالية"}
              />
            </Box>
          </Paper>

          {editingAdmin ? (
            <Alert severity="info" sx={{ mt: 2, borderRadius: 3 }}>
              يملك المدير جميع الواجهات والصلاحيات تلقائياً.
            </Alert>
          ) : (
            <>
              {/* مستوى الصلاحية */}
              {levelChoosable && (
                <Paper elevation={0} sx={{ p: 2.5, mt: 2, borderRadius: 3, border: "1px solid #e3e6ef" }}>
                  <SectionTitle
                    icon="🎚️"
                    title="نوع الحساب"
                    hint="يحدد ما يمكن إضافته لهذا الحساب من بوكسات وواجهات."
                  />
                  <AccessLevelPicker value={form.accessLevel} onChange={setAccessLevel} />
                </Paper>
              )}

              {ROLE_NOTES[rules.accessRole] && (
                <Alert severity={rules.accessRole === "locked" ? "warning" : "info"} sx={{ mt: 2, borderRadius: 3 }}>
                  {ROLE_NOTES[rules.accessRole]}
                </Alert>
              )}

              {/* البوكسات */}
              <Paper elevation={0} sx={{ p: 2.5, mt: 2, borderRadius: 3, border: "1px solid #e3e6ef" }}>
                <SectionTitle
                  icon="📦"
                  title="البوكسات"
                  count={activeBoxes.length}
                  hint="الأزرار التي تظهر في لوحة الموظف. اضغط ✕ لإزالة البوكس، أو اضغط على أي بوكس من القائمة المتاحة لإضافته."
                />
                <SubLabel>✅ البوكسات المفعّلة</SubLabel>
                {activeBoxes.length ? (
                  <TileGrid>
                    {activeBoxes.map((item) => (
                      <Tile key={item.id} item={item} onRemove={() => removeItem(item)} />
                    ))}
                  </TileGrid>
                ) : (
                  <Typography fontSize={14} color="text.secondary">
                    لا توجد بوكسات مفعّلة
                  </Typography>
                )}

                {addableBoxes.length > 0 && (
                  <>
                    <SubLabel>➕ بوكسات متاحة للإضافة</SubLabel>
                    <TileGrid>
                      {addableBoxes.map((item) => (
                        <Tile key={item.id} item={item} onAdd={() => addItem(item)} />
                      ))}
                    </TileGrid>
                  </>
                )}

                {lockedBoxes.length > 0 && (
                  <Typography fontSize={12} color="text.secondary" sx={{ mt: 2 }}>
                    🔒 غير متاحة لهذا الحساب: {lockedBoxes.map((i) => i.label).join("، ")}
                  </Typography>
                )}
              </Paper>

              {/* الواجهات - تظهر بس للحساب اللي تنضافله (بنين)، أو إذا عنده واجهة
                  قديمة حتى يكدر المدير يشيلها */}
              {(rules.dashboardsRule !== "none" || activeDashboards.length > 0) && (
              <Paper elevation={0} sx={{ p: 2.5, mt: 2, borderRadius: 3, border: "1px solid #e3e6ef" }}>
                <SectionTitle
                  icon="🖥️"
                  title="واجهات الموظفين"
                  count={activeDashboards.length}
                  hint={
                    rules.dashboardsRule === "free"
                      ? "واجهات الموظفين التي يمكن فتحها من الشريط العلوي."
                      : "إضافة واجهة موظف آخر تمنح الوصول إلى عمله ومعلوماته، لذلك تتطلب تأكيداً."
                  }
                />
                {access.builtIns?.home && (
                  <Chip
                    label="🏠 اللوحة الأساسية للحساب (ثابتة)"
                    size="small"
                    variant="outlined"
                    sx={{ mb: 1, fontWeight: 600 }}
                  />
                )}
                <SubLabel>✅ الواجهات المفعّلة</SubLabel>
                {activeDashboards.length ? (
                  <TileGrid>
                    {activeDashboards.map((item) => (
                      <Tile key={item.id} item={item} onRemove={() => removeItem(item)} />
                    ))}
                  </TileGrid>
                ) : (
                  <Typography fontSize={14} color="text.secondary">
                    لا توجد واجهات إضافية
                  </Typography>
                )}

                {rules.dashboardsRule === "none" ? (
                  <Typography fontSize={12} color="text.secondary" sx={{ mt: 2 }}>
                    🔒 لا يمكن إضافة واجهات موظفين لهذا الحساب
                  </Typography>
                ) : (
                  addableDashboards.length > 0 && (
                    <>
                      <SubLabel>
                        ➕ واجهات متاحة للإضافة
                        {rules.dashboardsRule === "confirm" && !dashboardsUnlocked && " (يظهر تنبيه قبل الإضافة)"}
                      </SubLabel>
                      <TileGrid>
                        {addableCurrent.map((item) => (
                          <Tile key={item.id} item={item} onAdd={() => addItem(item)} />
                        ))}
                      </TileGrid>
                      {addableArchived.length > 0 && (
                        <>
                          <Button
                            size="small"
                            onClick={() => setShowArchivedDashboards((v) => !v)}
                            sx={{ mt: 1.5, color: "text.secondary", fontWeight: 700 }}
                          >
                            📁 واجهات الموظفات السابقات ({addableArchived.length}) {showArchivedDashboards ? "▲" : "▼"}
                          </Button>
                          <Collapse in={showArchivedDashboards}>
                            <Box sx={{ mt: 1 }}>
                              <TileGrid>
                                {addableArchived.map((item) => (
                                  <Tile key={item.id} item={item} onAdd={() => addItem(item)} />
                                ))}
                              </TileGrid>
                            </Box>
                          </Collapse>
                        </>
                      )}
                    </>
                  )
                )}
              </Paper>
              )}

              {/* متقدم */}
              {dialog?.mode === "edit" && (
                <Box sx={{ mt: 2 }}>
                  <Button size="small" onClick={() => setShowAdvanced((v) => !v)} sx={{ color: "text.secondary" }}>
                    ⚙️ إعدادات متقدمة {showAdvanced ? "▲" : "▼"}
                  </Button>
                  <Collapse in={showAdvanced}>
                    <Paper elevation={0} sx={{ p: 2, mt: 1, borderRadius: 3, border: "1px solid #e3e6ef" }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={form.dashboardType === "boxes"}
                            onChange={(e) =>
                              setForm((f) => ({ ...f, dashboardType: e.target.checked ? "boxes" : "legacy" }))
                            }
                          />
                        }
                        label="بناء لوحة الموظف من البوكسات المختارة (لوحة جديدة باسمه الظاهر بدلاً من لوحته القديمة)"
                      />
                    </Paper>
                  </Collapse>
                </Box>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ borderTop: "1px solid #eee", px: 3, py: 1.5, gap: 1 }}>
          {isDirty && (
            <Typography fontSize={13} color="warning.main" fontWeight={700} sx={{ flex: 1 }}>
              ● توجد تغييرات غير محفوظة
            </Typography>
          )}
          <Button onClick={closeDialog} disabled={saving}>
            إلغاء
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || (dialog?.mode === "edit" && !isDirty)}
            sx={{ borderRadius: 2, px: 4, fontWeight: 800 }}
          >
            {saving ? "جارِ الحفظ..." : dialog?.mode === "create" ? "إنشاء الحساب" : "حفظ"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* تحذير إضافة واجهة موظف لغير بنين */}
      <Dialog open={!!pendingGrant} onClose={() => setPendingGrant(null)} dir="rtl" maxWidth="xs">
        <DialogTitle sx={{ color: "error.main", fontWeight: 900 }}>⚠️ تنبيه مهم</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "text.primary", fontWeight: 700 }}>
            هذا حساب موظف ({form.displayName || "حساب جديد"}) وليس حساب بنين.
          </DialogContentText>
          <DialogContentText sx={{ mt: 1 }}>
            إضافة واجهة «{pendingGrant?.label}» تمنحه الاطلاع على جميع أعمال ذلك الموظف ومعلوماته وأرقامه وحساباته، وهذا أمر حساس جداً.
          </DialogContentText>
          <DialogContentText sx={{ mt: 1, fontWeight: 700 }}>هل أنت متأكد من إضافة واجهة موظف آخر لهذا الحساب؟</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setPendingGrant(null)} autoFocus>
            لا، تراجع
          </Button>
          <Button
            color="error"
            onClick={() => {
              setDashboardsUnlocked(true);
              grantDashboard(pendingGrant);
              setPendingGrant(null);
            }}
          >
            نعم، أضف الواجهة
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default UserPermissionsDashboard;
